import { Editor, Node, Path } from 'slate';

import { resolveTableRowEntryByAnchorId } from './resolve-location-anchor';
import { stampReasoningOnFirstSourcedEdit } from './suggestion-reasoning';
import { serializeRemovedRowsToPreviewHtml } from '../../../serializers/html';
import { AlineTransforms, EditingMode, isTableRowElement } from '../../internal';
import type { SuggestionSource } from '../../types';

export interface ApplyRemoveRowsOutputOptions {
  /**
   * The author ID for the suggested edits (typically the AI or system user)
   */
  authorId: string;
  /**
   * Identifies the whole row removal as one group, so accept/reject acts on
   * it as a unit
   */
  groupId: string;
  /**
   * The `row-…` anchor ids of the rows to remove — one contiguous run of rows
   * in a single table (the shape `partitionRemoveRowAnchorsIntoRuns`
   * produces).
   */
  locationAnchors: string[];
  /**
   * Why the rows are being removed — stored on the first sourced edit of the
   * removal
   */
  reasoning: string;
  /**
   * The source stamp carried by every marked edit — or undefined to
   * materialize the removal as the author's own tracked changes, with no
   * source stamps and no stored reasoning (no AI chrome).
   */
  source: SuggestionSource | undefined;
}

/**
 * Applies a `remove-rows` structured edit output to the editor as a suggested
 * removal of the table rows identified by their `row-…` anchor ids — the
 * AI-sourced equivalent of the "Delete row" context-menu command in
 * suggesting mode.
 *
 * The flow:
 * 1. Resolves each anchor id to its row (throws when an anchor is not a row
 *    id or matches no row) and spans the run from the first to the last row.
 * 2. Marks every row in the span with a sourced `removeTableRow` edit and
 *    strikes its cell content, via `deleteTableRows` in SUGGESTING mode.
 *    Accepting removes the rows — and the table itself once its last row
 *    goes; rejecting restores them.
 * 3. Stores the reasoning on the first sourced edit of the span. Block
 *    suggestions carry no suggestion-marker pair — the sourced edits
 *    themselves are the suggestion, resolved as one unit by groupId.
 *
 * Returns the removed rows' plain text (one line per row) for the chat card
 * preview.
 */
export function applyRemoveRowsOutput(
  editor: Editor,
  options: ApplyRemoveRowsOutputOptions
): { removedHtml: string } {
  const { authorId, groupId, locationAnchors, reasoning, source } = options;

  const rowPaths = resolveRowPaths(editor, locationAnchors);
  const firstRowPath = rowPaths.at(0);
  const lastRowPath = rowPaths.at(-1);
  if (!firstRowPath || !lastRowPath) {
    throw new Error('remove-rows requires at least one row id');
  }

  const rows = rowPaths
    .map(rowPath => Node.get(editor, rowPath))
    .filter(node => isTableRowElement(node));
  const removedHtml = serializeRemovedRowsToPreviewHtml(rows);

  AlineTransforms.deleteTableRows(editor, {
    at: Editor.range(editor, Editor.start(editor, firstRowPath), Editor.end(editor, lastRowPath)),
    authorId,
    editingMode: EditingMode.SUGGESTING,
    groupId,
    source,
  });

  // A sourceless removal has no sourced edit to stamp — and no AI reasoning
  // belongs on the author's own tracked changes.
  if (source) {
    stampReasoningOnFirstSourcedEdit(editor, {
      at: Editor.range(editor, firstRowPath, lastRowPath),
      groupId,
      reasoning,
    });
  }

  return { removedHtml };
}

/**
 * A maximal run of contiguous rows in one table, expressed as the
 * locationAnchors that identified them — the input shape
 * `applyRemoveRowsOutput` takes.
 */
export interface RemoveRowAnchorRun {
  locationAnchors: string[];
}

/**
 * Partitions a set of `row-…` anchor ids into runs of contiguous rows within
 * one table, in document order regardless of the order the LLM listed them.
 * A gap row that no anchor identifies splits the removal into independent
 * runs, as does a different table. An anchor that is not a row id or matches
 * no row throws.
 */
export function partitionRemoveRowAnchorsIntoRuns(
  editor: Editor,
  locationAnchors: string[]
): RemoveRowAnchorRun[] {
  const entries = [...new Set(locationAnchors)]
    .map(anchor => ({ anchor, rowPath: resolveRowPath(editor, anchor) }))
    .sort((a, b) => Path.compare(a.rowPath, b.rowPath));

  const runs: RemoveRowAnchorRun[] = [];
  let currentRowPath: Path | null = null;
  for (const entry of entries) {
    const isSameRow = currentRowPath && Path.equals(currentRowPath, entry.rowPath);
    const isNextRowInSameTable =
      currentRowPath && Path.equals(Path.next(currentRowPath), entry.rowPath);
    const currentRun = runs.at(-1);
    if (currentRun && currentRowPath && (isSameRow || isNextRowInSameTable)) {
      currentRun.locationAnchors.push(entry.anchor);
    } else {
      runs.push({ locationAnchors: [entry.anchor] });
    }
    currentRowPath = entry.rowPath;
  }
  return runs;
}

function resolveRowPaths(editor: Editor, locationAnchors: string[]): Path[] {
  const rowPaths: Path[] = [];
  for (const anchor of locationAnchors) {
    const rowPath = resolveRowPath(editor, anchor);
    if (rowPaths.some(existing => Path.equals(existing, rowPath))) continue;
    rowPaths.push(rowPath);
  }
  return rowPaths.sort(Path.compare);
}

function resolveRowPath(editor: Editor, locationAnchor: string): Path {
  return resolveTableRowEntryByAnchorId(editor, locationAnchor)[1];
}
